import { useState, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { FaBell } from 'react-icons/fa';
import { useNotifications } from '../hooks/useNotifications';
import { markAsRead } from '../api/notifications';
import { Tooltip } from './Tooltip';
import { getBadgeTailwindClasses } from '../styles/designSystem';

export const NotificationButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, loading, error, refresh } = useNotifications();
  const { keycloak } = useKeycloak();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (e: React.MouseEvent, notification: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (keycloak.token) {
      try {
        await markAsRead(keycloak.token, notification.id);
        refresh();
      } catch (err) {
        console.error("Failed to mark notification as read", err);
      }
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content="Notifications">
        <button 
          onClick={() => setIsOpen(!isOpen)} 
          className="relative p-2 rounded-full hover:bg-gray-50 focus:outline-none transition-colors"
        >
          <FaBell className="w-5 h-5 text-gray-500" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 block h-3 w-3 rounded-full bg-blue-500 ring-1 ring-white" />
          )}
        </button>
      </Tooltip>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-gray-100 rounded-lg shadow-lg z-50">
          <div className="px-4 py-3 border-b border-gray-50">
            <h3 className="text-sm font-medium text-gray-700">Notifications <span className={getBadgeTailwindClasses('dev')}>dev</span></h3>
          </div>
          
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Loading...</div>
          )}
          
          {error && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">{error}</div>
          )}
          
          {!loading && !error && notifications.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">No new notifications</div>
          )}
          
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <div 
                key={notification.id} 
                className={`px-4 py-3 border-b border-gray-50 last:border-b-0 ${
                  !notification.is_read ? 'bg-gray-25' : ''
                } hover:bg-gray-25 transition-colors cursor-pointer`}
                onClick={() => !notification.is_read && handleMarkAsRead({} as React.MouseEvent, notification)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className={`w-2 h-2 rounded-full ${
                      !notification.is_read ? 'bg-blue-400' : 'bg-gray-200'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 